import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type TeamsAccessJSON = z.infer<typeof TeamsAccessSchema.TeamsAccessJSONZ>;

export type TeamsAccessParams = z.infer<typeof TeamsAccessSchema.TeamsAccessZ>;

export default class TeamsAccessSchema {
  public static readonly TeamsAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.TeamsAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly TeamsAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.TeamsAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): TeamsAccessParams {
    return parseOrThrow(TeamsAccessSchema.TeamsAccessZ, values, {
      logError: true,
      schemaName: "TeamsAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): TeamsAccessJSON {
    return parseOrThrow(TeamsAccessSchema.TeamsAccessJSONZ, values, {
      logError: true,
      schemaName: "TeamsAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is TeamsAccessParams {
    try {
      TeamsAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is TeamsAccessJSON {
    try {
      TeamsAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: TeamsAccessJSON): TeamsAccessParams {
    const parsed = parseOrThrow(TeamsAccessSchema.TeamsAccessJSONZ, data, {
      logError: true,
      schemaName: "TeamsAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return TeamsAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: TeamsAccessParams): TeamsAccessJSON {
    const parsed = TeamsAccessSchema.validate(notification);
    return TeamsAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(TeamsAccessSchema.TeamsAccessJSONZ, TeamsAccessSchema.TeamsAccessZ, {
    decode: (data) => ({
      ...PermissionResourceSchema.deserialize(data),
      resourceType: ResourceType.TeamsAccess as const,
      accessLevel: data.accessLevel ?? undefined,
      scope: data.scope ?? undefined,
    }),
    encode: (resource) => ({
      ...PermissionResourceSchema.serialize(resource),
      resourceType: ResourceType.TeamsAccess as const,
      accessLevel: resource.accessLevel,
      scope: resource.scope,
    }),
  });
}
